import React, { useMemo, useState } from 'react';
import { arrayOf, func, number, shape, string } from 'prop-types';
import { toast } from 'react-toastify';
import { FormContextProvider } from '@stormwater-software/lib-form';

import FormModal from '../../Shared/form/modal/FormModal';
import projectApi from '../../../utils/api/project';
import { withOfflineWrite } from '../../../utils/offlineFallback';
import { placeFindingOffline } from '../../../utils/offlineFindingPlacement';

const SEARCH_THRESHOLD = 8;

const mapLabel = (m) => m.name || `Map #${m.project_upload_id}`;

function PlaceFindingOnMapsModalUI({
  projectId,
  finding,
  coords,
  maps,
  inspectionId,
  onClose,
}) {
  const [selected, setSelected] = useState(() =>
    maps.map((m) => m.project_upload_id),
  );
  const [query, setQuery] = useState('');

  const toggle = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const allSelected = selected.length === maps.length;

  const toggleAll = () =>
    setSelected(allSelected ? [] : maps.map((m) => m.project_upload_id));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return q ? maps.filter((m) => mapLabel(m).toLowerCase().includes(q)) : maps;
  }, [maps, query]);

  const handleSubmit = async () => {
    if (!selected.length) {
      onClose();

      return;
    }

    const toastId = toast.loading('Placing finding on map...');

    try {
      const body = {
        lat: coords.lat,
        lng: coords.lng,
        project_upload_ids: selected,
      };

      // Offline the markers are written locally and queued for the next sync.
      await withOfflineWrite(
        () => projectApi.placeFindingOnMaps(projectId, finding.id, body),
        () => placeFindingOffline(projectId, finding.id, body, inspectionId),
      );
      toast.update(toastId, {
        render: navigator.onLine
          ? 'Finding placed on map'
          : 'Finding placed on map — it will sync when you are back online',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
    } catch (err) {
      toast.update(toastId, {
        // Offline the reason is ours and worth showing; online it is an axios string.
        render: !navigator.onLine && err?.message
          ? err.message
          : 'Could not place finding on map',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }

    onClose();
  };

  return (
    <FormModal
      modalProps={{ title: 'Place finding on map', isSmall: true }}
      submitText='Add'
      cancelText='Skip'
      onCancel={onClose}
      onSubmit={handleSubmit}
    >
      <p className='place-finding-maps-intro'>
        This finding&rsquo;s location falls within{' '}
        {maps.length === 1
          ? 'a georeferenced map'
          : `${maps.length} georeferenced maps`}
        . Select where to place its marker:
      </p>

      <div className='place-finding-maps-toolbar'>
        <button
          type='button'
          className='place-finding-maps-selectall'
          onClick={toggleAll}
        >
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
        <span className='place-finding-maps-count'>
          {selected.length} of {maps.length} selected
        </span>
      </div>

      {maps.length > SEARCH_THRESHOLD && (
        <input
          type='text'
          className='place-finding-maps-search'
          placeholder='Search maps…'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}

      <ul className='place-finding-maps-list'>
        {filtered.map((m) => {
          const id = m.project_upload_id;
          const checked = selected.includes(id);

          return (
            <li
              key={id}
              className={`place-finding-maps-item${
                checked ? ' is-selected' : ''
              }`}
              onClick={() => toggle(id)}
            >
              <input
                type='checkbox'
                className='place-finding-maps-check'
                checked={checked}
                onChange={() => toggle(id)}
                onClick={(e) => e.stopPropagation()}
              />
              <span className='place-finding-maps-name' title={mapLabel(m)}>
                {mapLabel(m)}
              </span>
            </li>
          );
        })}
        {!filtered.length && (
          <li className='place-finding-maps-empty'>
            No maps match your search.
          </li>
        )}
      </ul>
    </FormModal>
  );
}

function PlaceFindingOnMapsModal(props) {
  return (
    <FormContextProvider>
      <PlaceFindingOnMapsModalUI {...props} />
    </FormContextProvider>
  );
}

const mapShape = shape({
  project_upload_id: number.isRequired,
  name: string,
});

PlaceFindingOnMapsModalUI.propTypes = {
  projectId: number.isRequired,
  finding: shape({ id: number }).isRequired,
  coords: shape({ lat: number, lng: number }).isRequired,
  maps: arrayOf(mapShape).isRequired,
  inspectionId: number,
  onClose: func.isRequired,
};

PlaceFindingOnMapsModal.propTypes = PlaceFindingOnMapsModalUI.propTypes;

export default PlaceFindingOnMapsModal;
